import jsonExport from "jsonexport/dist";
import { useEffect, useState } from "react";
import {
  downloadCSV,
  InfiniteListBase,
  useGetIdentity,
  useListContext,
  useTranslate,
  type Exporter,
} from "ra-core";
import { LayoutList, Table2 } from "lucide-react";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { BulkDeleteButton } from "@/components/admin/bulk-delete-button";
import { BulkExportButton } from "@/components/admin/bulk-export-button";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { SortButton } from "@/components/admin/sort-button";
import { Card } from "@/components/ui/card";

import type { Company, Contact, Sale, Tag } from "../types";
import { BulkTagButton } from "./BulkTagButton";
import { ContactEmpty } from "./ContactEmpty";
import { DataImportButton } from "../dataImport/DataImportButton";
import {
  ContactListContent,
  ContactListContentMobile,
} from "./ContactListContent";
import { ClayContactsTable } from "./ClayContactsTable";
import { BulkGhostEnrichButton } from "./LinkedInCrmWidgets";
import { JobChangeRadarButton } from "./JobChangeRadarModal";
import {
  ContactListFilterSummary,
  ContactListFilter,
} from "./ContactListFilter";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";

export const ContactList = () => {
  const { identity } = useGetIdentity();
  const [viewMode, setViewMode] = useState<"clay" | "list">("clay");

  if (!identity) return null;

  return (
    <List
      title={false}
      actions={<ContactListActions viewMode={viewMode} setViewMode={setViewMode} />}
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
    >
      <ContactListLayoutDesktop viewMode={viewMode} />
    </List>
  );
};

const ContactListLayoutDesktop = ({
  viewMode,
}: {
  viewMode: "clay" | "list";
}) => {
  const { data, isPending, filterValues } = useListContext<Contact>();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  if (viewMode === "clay") {
    return (
      <div className="flex flex-col gap-4">
        <ClayContactsTable />
        <BulkActionsToolbar>
          <ContactBulkActionButtons />
        </BulkActionsToolbar>
      </div>
    );
  }

  return (
    <div className="flex flex-row gap-8">
      <ContactListFilter />
      <div className="w-full flex flex-col gap-4">
        <Card className="py-0">
          <ContactListContent />
        </Card>
      </div>
      <BulkActionsToolbar>
        <ContactBulkActionButtons />
      </BulkActionsToolbar>
    </div>
  );
};

const ContactBulkActionButtons = () => {
  const { data, selectedIds } = useListContext<Contact>();
  const selectedContacts = (data ?? []).filter((c) =>
    selectedIds.includes(c.id),
  );
  return (
    <>
      <SelectAllButton />
      <BulkGhostEnrichButton contacts={selectedContacts} />
      <BulkTagButton />
      <BulkExportButton />
      <BulkDeleteButton />
    </>
  );
};

const ContactListActions = ({
  viewMode,
  setViewMode,
}: {
  viewMode: "clay" | "list";
  setViewMode: (mode: "clay" | "list") => void;
}) => {
  const { data } = useListContext<Contact>();
  return (
    <TopToolbar>
      <div className="inline-flex items-center rounded-lg border bg-muted/40 p-0.5 text-xs">
        <button
          type="button"
          onClick={() => setViewMode("clay")}
          className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition-colors cursor-pointer ${
            viewMode === "clay"
              ? "bg-background text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Table2 className="size-3.5 text-indigo-500" />
          <span>Clay AI Table</span>
        </button>
        <button
          type="button"
          onClick={() => setViewMode("list")}
          className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition-colors cursor-pointer ${
            viewMode === "list"
              ? "bg-background text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <LayoutList className="size-3.5" />
          <span>Classic List</span>
        </button>
      </div>
      <JobChangeRadarButton />
      <BulkGhostEnrichButton contacts={data ?? []} />
      <SortButton fields={["first_name", "last_name", "last_seen"]} />
      <DataImportButton resource="contacts" />
      <ExportButton exporter={exporter} />
      <CreateButton />
    </TopToolbar>
  );
};

export const ContactListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const translate = useTranslate();
  const { isPending, data, error, filterValues, displayedFilters, setFilters } =
    useListContext();

  const { q, ...filtersWithoutSearch } = filterValues ?? {};
  useEffect(() => {
    if (q != null) {
      setFilters(filtersWithoutSearch, displayedFilters);
    }
  });

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div>
      <MobileHeader showRefresh>
        <div className="flex flex-1 items-center justify-between">
          <h1 className="text-xl font-semibold">
            {translate("resources.contacts.name", { smart_count: 2 })}
          </h1>
          <ContactListFilter />
        </div>
      </MobileHeader>
      <MobileContent>
        <ContactListFilterSummary />
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");
  const tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");

  const contacts = records.map((contact) => {
    const exportedContact = {
      ...contact,
      company:
        contact.company_id != null
          ? companies[contact.company_id].name
          : undefined,
      sales:
        contact.sales_id != null
          ? `${sales[contact.sales_id].first_name} ${sales[contact.sales_id].last_name}`
          : undefined,
      tags: contact.tags.map((tagId) => tags[tagId].name).join(", "),
      email_work: contact.email_jsonb?.find((email) => email.type === "Work")
        ?.email,
      email_home: contact.email_jsonb?.find((email) => email.type === "Home")
        ?.email,
      email_other: contact.email_jsonb?.find((email) => email.type === "Other")
        ?.email,
      email_jsonb: JSON.stringify(contact.email_jsonb),
      email_fts: undefined,
      phone_work: contact.phone_jsonb?.find((phone) => phone.type === "Work")
        ?.number,
      phone_home: contact.phone_jsonb?.find((phone) => phone.type === "Home")
        ?.number,
      phone_other: contact.phone_jsonb?.find((phone) => phone.type === "Other")
        ?.number,
      phone_jsonb: JSON.stringify(contact.phone_jsonb),
      phone_fts: undefined,
    };
    delete exportedContact.email_fts;
    delete exportedContact.phone_fts;
    return exportedContact;
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "contacts");
  });
};
